import repository from '@/repository'
import { ActionTree, GetterTree, Module, MutationTree } from 'vuex'
import { RootState } from '@/store/types'

export interface AuditLog {
  activityId: number
  userId: number | null
  userName: string | null
  role: string | null
  locationId: number | null
  locationName: string | null
  deviceId: number | null
  deviceName: string | null
  entityId: number | null
  entityName: string | null
  entityTypeId: number | null
  entityTypeName: string | null
  actionTypeId: number | null
  actionTypeName: string | null
  result: boolean | null
  timestamp: string
}

export interface ActionType {
  entityActionTypeId: number
  entityActionTypeName: string
  entityTypeId: number
  entityTypeName: string
}

export interface AuditLogsFilter {
  CustomerId: number
  UserId: number | null
  EntityTypeId: number | null
  EntityActionTypeId: number | null
  LocationId: number | null
  DeviceId: number | null
  Result: boolean | null
  StartDateTime: string | null
  EndDateTime: string | null
  Keyword?: string | null
}

export interface AuditLogsQuery extends AuditLogsFilter {
  CurrentPage: number
  PageSize: number
  IncludeAllPages: boolean | null
}

interface AuditLogsState {
  actionTypes: ActionType[]
  actionTypesIsLoading: boolean
  auditLogs: AuditLog[]
  auditLogsIsLoading: boolean
  exportAuditLogsIsLoading: boolean
  totalCount: number
  currentPage: number
  totalPages: number
}

const getDefaultState = (): AuditLogsState => ({
  actionTypes: [],
  actionTypesIsLoading: false,
  auditLogs: [],
  auditLogsIsLoading: false,
  exportAuditLogsIsLoading: false,
  totalCount: 0,
  currentPage: 1,
  totalPages: 1,
})

const state = getDefaultState()

const getters: GetterTree<AuditLogsState, RootState> = {
  auditLogs: (state) => state.auditLogs,
  actionTypes: (state) => state.actionTypes,
}

const mutations: MutationTree<AuditLogsState> = {
  reset(state) {
    Object.assign(state, getDefaultState())
  },
  setActionTypes(state, value: ActionType[]) {
    state.actionTypes = value
  },
  setActionTypesIsLoading(state, value: boolean) {
    state.actionTypesIsLoading = value
  },
  setAuditLogs(state, value: AuditLog[]) {
    state.auditLogs = value
  },
  setAuditLogsIsLoading(state, value: boolean) {
    state.auditLogsIsLoading = value
  },
  setExportAuditLogsIsLoading(state, value: boolean) {
    state.exportAuditLogsIsLoading = value
  },
  setPagination(state, value) {
    state.totalCount = value.totalCount || 0
    state.currentPage = value.currentPage || 1
    state.totalPages = value.totalPages || 1
  },
}

const actions: ActionTree<AuditLogsState, RootState> = {
  getActionTypes({ commit }) {
    commit('setActionTypesIsLoading', true)
    return repository.CustomerActivity.GetActionTypes({})
      .then((res) => commit('setActionTypes', res.data))
      .finally(() => commit('setActionTypesIsLoading', false))
  },
  getAuditLogs({ commit }, payload: AuditLogsQuery) {
    commit('setAuditLogsIsLoading', true)
    return repository.CustomerActivity.GetCustomerActivities(payload)
      .then((res) => {
        commit('setAuditLogs', res.data.items || [])
        commit('setPagination', res.data)
      })
      .finally(() => commit('setAuditLogsIsLoading', false))
  },
  exportAuditLogs({ commit }, payload: AuditLogsFilter) {
    commit('setExportAuditLogsIsLoading', true)
    return repository.CustomerActivity.ExportCsv(payload).finally(() =>
      commit('setExportAuditLogsIsLoading', false)
    )
  },
}

const AuditLogs: Module<AuditLogsState, RootState> = {
  namespaced: true,
  state,
  mutations,
  actions,
  getters,
}

export default AuditLogs
